/*
 * Surgical Suite — IPD Post-Op Discharge Summary Composer (v2 · Document Edition)
 *
 * Renders as a real hospital A4 discharge summary sheet (approved mock design).
 * - Hospital field is FROZEN to the surgeon's configured Unit Settings
 *   postings (dropdown when multiple; never free-text / never voice).
 * - Master conversational dictation: one switch; cue words route speech to
 *   the right section; live interim streaming; background Gemini cleanup.
 * - Footer: resident signatory + principal surgeon + QR verification +
 *   hospital branding + "Built on the DocSite platform".
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Mic, MicOff, Loader2, ArrowLeft, Save, Lock, Printer, Plus, X,
  UserPlus, ShieldCheck, ShieldOff, Trash2, ClipboardList,
  FileDown, KeyRound, Image as ImageIcon, BarChart3, Share2, Wifi, WifiOff,
  Copy, RefreshCcw, Settings2, Upload, FileText, HeartPulse,
} from 'lucide-react';
import api from '@/lib/api';
import SurgicalGate from './SurgicalGate';
import RecoveryPanel from '@/components/surgical/RecoveryPanel';
import RecoveryMonitorButton from '@/components/surgical/RecoveryMonitorButton';
import { toast } from 'sonner';
import localforage from 'localforage';
import QRCode from 'qrcode';

const NAVY = '#07122f';
const TEAL = '#007f73';
const PAPER = '#fdfcf7';
const serif = "'Playfair Display', 'Merriweather', Georgia, serif";
const mono = "'JetBrains Mono', 'IBM Plex Mono', monospace";

const draftStore = localforage.createInstance({ name: 'docsite-surgical', storeName: 'drafts' });
const DRAFT_KEY = 'active-discharge-draft';
const syncQueue = localforage.createInstance({ name: 'docsite-surgical', storeName: 'sync-queue' });

// Cue-word routing table. Hospital + CR No are deliberately ABSENT —
// they are frozen configuration, never voice-editable.
const CUES = [
  { keys: ['operative findings', 'findings'], target: 'operative_findings' },
  { keys: ['operation performed', 'operation'], target: 'operation_performed' },
  { keys: ['post operative plan', 'post-op plan', 'post op plan', 'postoperative plan'], target: 'post_op_plan' },
  { keys: ['treatments advised', 'treatment advised', 'treatments', 'treatment'], target: 'treatments_advised' },
  { keys: ['follow up', 'follow-up', 'followup'], target: 'followup_schedule' },
  { keys: ['diagnosis'], target: 'diagnosis' },
  { keys: ['patient name'], target: 'patient_name' },
  { keys: ['patient address', 'address'], target: 'patient_address' },
  { keys: ['admitted under'], target: 'principal_surgeon_name' },
  { keys: ['patient mobile', 'mobile'], target: 'patient_mobile' },
  { keys: ['patient age', 'age'], target: 'patient_age' },
  { keys: ['patient sex', 'sex'], target: 'patient_sex' },
];
const BULLETED = new Set(['operative_findings', 'treatments_advised']);
const FIELD_LABELS = {
  diagnosis: 'Diagnosis', operation_performed: 'Operation Performed',
  operative_findings: 'Operative Findings', post_op_plan: 'Post-Operative Plan',
  treatments_advised: 'Treatments Advised', followup_schedule: 'Follow-up Schedule',
  patient_name: 'Patient Name', patient_address: 'Patient Address',
  principal_surgeon_name: 'Admitted Under', patient_mobile: 'Mobile',
  patient_age: 'Age', patient_sex: 'Sex',
};

function useContinuousDictation({ onSegment, onInterim, enabled }) {
  const recRef = useRef(null);
  const [supported, setSupported] = useState(true);
  const [live, setLive] = useState(false);

  const start = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); toast.error('Voice input is not supported on this browser'); return false; }
    if (recRef.current) return true;
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = 'en-IN';
    r.onresult = (evt) => {
      let finalChunk = '';
      let interim = '';
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        if (evt.results[i].isFinal) finalChunk += evt.results[i][0].transcript + ' ';
        else interim += evt.results[i][0].transcript + ' ';
      }
      if (interim.trim()) onInterim(interim.trim());
      if (finalChunk.trim()) onSegment(finalChunk.trim());
    };
    r.onend = () => { if (recRef.current) { try { r.start(); } catch {} } };  // auto-restart while live
    r.onerror = (e) => { if (e.error === 'not-allowed') { setLive(false); recRef.current = null; toast.error('Microphone permission denied'); } };
    recRef.current = r;
    try { r.start(); setLive(true); return true; } catch { return false; }
  }, [onSegment, onInterim]);

  const stop = useCallback(() => {
    const r = recRef.current;
    recRef.current = null;
    if (r) { try { r.onend = null; r.stop(); } catch {} }
    setLive(false);
  }, []);

  useEffect(() => () => { const r = recRef.current; recRef.current = null; if (r) { try { r.stop(); } catch {} } }, []);
  return { start, stop, live, supported };
}

// ─────────────────────────────────────────────────────────────────────
export default function SurgicalDischargePage() {
  const emptyForm = {
    hospital_name: '', department: '', location: '',
    patient_name: '', patient_sex: '', patient_age: '', patient_cr_no: '', patient_address: '', patient_mobile: '',
    principal_surgeon_name: '', co_residents: [],
    surgery_catalog_id: '', operation_performed: '',
    diagnosis: '', operative_findings: [], post_op_plan: '',
    treatments_advised: [], followup_schedule: [], intra_op_photos: [], attachments: [],
  };
  const [form, setForm] = useState(emptyForm);
  const [showRecovery, setShowRecovery] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dischargeId, setDischargeId] = useState('');
  const [viewToken, setViewToken] = useState('');
  const [status, setStatus] = useState('draft');
  const [showCatalog, setShowCatalog] = useState(false);
  const [newSurgeryName, setNewSurgeryName] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [sessionToken, setSessionToken] = useState('');
  const [sessionExpiresAt, setSessionExpiresAt] = useState('');
  const [verifyingChallenge, setVerifyingChallenge] = useState(false);
  const [casesOpen, setCasesOpen] = useState(false);
  const [dischargesList, setDischargesList] = useState([]);
  const [compactPdf, setCompactPdf] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [residentReg, setResidentReg] = useState('');
  const [entitlement, setEntitlement] = useState(null); // null=loading, object=known
  const refreshEntitlement = useCallback(async () => {
    try { const { data } = await api.get('/surgical/entitlement/status'); setEntitlement(data); }
    catch { setEntitlement({ active: false, reason: 'no_entitlement', list_price_inr: 5000, upi_vpa: 'vivektikoo@okhdfcbank', upi_payee_name: 'DocSite Clinical Management Platform' }); }
  }, []);
  useEffect(() => { refreshEntitlement(); }, [refreshEntitlement]);
  const [settings, setSettings] = useState({ hospitals: [], default_hospital_id: '' });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedHospitalId, setSelectedHospitalId] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  // Master dictation state
  const [activeTarget, setActiveTarget] = useState('diagnosis');
  const [interimText, setInterimText] = useState('');
  const [lastCue, setLastCue] = useState('');
  const cleanupTimer = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const profileId = activeProfile?.id || null;

  // ── Dictation engine ──
  const routeSegment = useCallback((raw) => {
    let text = raw;
    let target = activeTarget;
    // Cue detection: phrase at (or near) the start switches the field.
    const lower = raw.toLowerCase();
    for (const cue of CUES) {
      for (const k of cue.keys) {
        const idx = lower.indexOf(k);
        if (idx !== -1 && idx < 12) {
          target = cue.target;
          setActiveTarget(cue.target);
          setLastCue(FIELD_LABELS[cue.target]);
          text = raw.slice(idx + k.length).replace(/^[:\-\s,.]+/, '');
          break;
        }
      }
    }
    if (!text.trim()) { setInterimText(''); return; }
    if (BULLETED.has(target)) {
      const parts = text.split(/(?:\.\s+|\bnext\b[,:]?\s*)/i).map((s) => s.trim()).filter(Boolean);
      if (parts.length) set(target, [...(form[target] || []), ...parts]);
    } else if (target === 'followup_schedule') {
      set('followup_schedule', [...(form.followup_schedule || []), { date: '', action: text }]);
    } else {
      const prev = String(form[target] || '').trim();
      set(target, prev ? `${prev} ${text}` : text);
    }
    setInterimText('');
    // Background Gemini cleanup for the field just spoken (non-blocking).
    clearTimeout(cleanupTimer.current);
    cleanupTimer.current = setTimeout(() => polishField(target), 2500);
  }, [activeTarget, form]);

  const polishField = async (target) => {
    const raw = BULLETED.has(target) ? (form[target] || []).join('\n') : String(form[target] || '');
    if (!raw.trim()) return;
    try {
      const { data } = await api.post('/surgical/voice/parse', {
        transcript: raw, target, profile_id: profileId, session_token: sessionToken || null,
      });
      if (data?.value && (Array.isArray(data.value) ? data.value.length : String(data.value).trim())) {
        set(target, data.value);
      }
    } catch (e) {
      if (e?.response?.status === 401) { setSessionToken(''); setSessionExpiresAt(''); }
    }
  };

  const dictation = useContinuousDictation({
    onSegment: routeSegment,
    onInterim: setInterimText,
  });

  const toggleDictation = () => {
    if (dictation.live) { dictation.stop(); setInterimText(''); return; }
    if (profileId && !sessionToken) { toast.error('Verify your voice challenge before dictating'); return; }
    dictation.start();
  };

  // ── Offline draft restore / autosave ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cached = await draftStore.getItem(DRAFT_KEY);
        const cachedHasContent = cached && (cached.dischargeId || (cached.form || {}).patient_name || (cached.form || {}).operation_performed);
        if (cachedHasContent && alive) {
          setForm(cached.form || emptyForm);
          setDischargeId(cached.dischargeId || '');
          setViewToken(cached.viewToken || '');
          setStatus(cached.status || 'draft');
          setSelectedHospitalId(cached.selectedHospitalId || '');
          if (cached.dischargeId) {
            // Sync meta that isn't part of the cached draft (demo flag, resident reg no).
            try {
              const { data: meta } = await api.get(`/surgical/discharge/${cached.dischargeId}`);
              if (alive) { setIsDemo(!!meta.is_demo); setResidentReg(meta.resident_reg_no || ''); }
            } catch {}
          }
        } else if (alive) {
          // Fresh visit, no local draft — open the latest discharge as the starting view.
          try {
            const { data } = await api.get('/surgical/discharge/list');
            const latest = (data.rows || [])[0];
            if (latest && alive) drillIntoDischarge(latest.id);
          } catch {}
        }
      } catch {}
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const id = setTimeout(() => {
      draftStore.setItem(DRAFT_KEY, { form, dischargeId, viewToken, status, selectedHospitalId, savedAt: Date.now() }).catch(() => {});
    }, 800);
    return () => clearTimeout(id);
  }, [form, dischargeId, viewToken, status, selectedHospitalId]);

  useEffect(() => {
    if (!sessionExpiresAt) return;
    const ms = new Date(sessionExpiresAt).getTime() - Date.now();
    if (ms <= 0) { setSessionToken(''); setSessionExpiresAt(''); return; }
    const t = setTimeout(() => {
      setSessionToken(''); setSessionExpiresAt('');
      toast.info('Voice session expired — record the challenge phrase again');
      if (dictation.live) dictation.stop();
    }, ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionExpiresAt]);

  const loadProfiles = async () => {
    try { const { data } = await api.get('/surgical/voice-profile/list'); setProfiles(data.rows || []); } catch {}
  };
  const loadCatalog = async () => {
    try { const { data } = await api.get('/surgical/catalog/list'); setCatalog(data.rows || []); } catch {}
  };
  const loadSettings = async () => {
    try {
      const { data } = await api.get('/surgical/settings');
      setSettings(data);
      // Pre-fill "Admitted Under" from the logged-in surgeon's profile (never overwrites a typed/draft value).
      const principalLine = [data.principal_name, data.principal_qualifications].filter(Boolean).join(', ');
      if (principalLine) setForm((f) => (f.principal_surgeon_name ? f : { ...f, principal_surgeon_name: principalLine }));
      // Apply frozen hospital on first load (unless a draft already picked one).
      if (!selectedHospitalId && (data.hospitals || []).length) {
        const def = data.hospitals.find((h) => h.id === data.default_hospital_id) || data.hospitals[0];
        applyHospital(def);
      }
    } catch {}
  };
  useEffect(() => { loadProfiles(); loadCatalog(); loadSettings(); }, []);  // eslint-disable-line

  const applyHospital = (h) => {
    if (!h) return;
    setSelectedHospitalId(h.id);
    set('hospital_name', h.name);
    if (!form.department) set('department', h.department || '');
    if (!form.location) set('location', h.location || '');
  };

  const selectedHospital = (settings.hospitals || []).find((h) => h.id === selectedHospitalId)
    || (settings.hospitals || [])[0] || null;

  // ── QR verification (sealed docs only) ──
  useEffect(() => {
    if (status === 'sealed' && viewToken) {
      QRCode.toDataURL(`${window.location.origin}/surgical-verify/${viewToken}`, { margin: 0, width: 120, color: { dark: NAVY } })
        .then(setQrDataUrl).catch(() => setQrDataUrl(''));
    } else { setQrDataUrl(''); }
  }, [status, viewToken]);

  const addSurgery = async () => {
    const n = newSurgeryName.trim();
    if (n.length < 2) return;
    try {
      const { data } = await api.post('/surgical/catalog/add', { surgery_name: n, synonyms: [], default_findings_template: '', default_post_op_template: '', is_active: true });
      toast.success('Added to your catalog'); setNewSurgeryName(''); await loadCatalog();
      set('surgery_catalog_id', data.id); set('operation_performed', data.surgery_name);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add surgery'); }
  };
  const pickSurgery = (id) => {
    const s = catalog.find((c) => c.id === id); if (!s) return;
    set('surgery_catalog_id', id); set('operation_performed', s.surgery_name);
    if (s.default_findings_template && (form.operative_findings || []).length === 0)
      set('operative_findings', s.default_findings_template.split('\n').filter((x) => x.trim()));
    if (s.default_post_op_template && !form.post_op_plan) set('post_op_plan', s.default_post_op_template);
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      if (dischargeId) {
        try {
          await api.put(`/surgical/discharge/${dischargeId}`, form);
          toast.success('Draft updated');
        } catch (netErr) {
          const st = netErr?.response?.status;
          if (st === 402) {
            const reason = netErr?.response?.data?.detail?.reason || 'no_entitlement';
            await refreshEntitlement();
            toast.error(reason === 'monthly_cap_exhausted'
              ? 'Monthly discharge cap reached — enter a new code to continue'
              : 'Surgical Suite access has expired — enter a new access code');
            return;
          }
          if (!navigator.onLine || netErr?.code === 'ERR_NETWORK') {
            await syncQueue.setItem(`upd-${Date.now()}`, { url: `/surgical/discharge/${dischargeId}`, method: 'PUT', data: form });
            await refreshPending();
            toast.info('Offline — draft queued and will sync when back online');
          } else { throw netErr; }
        }
      } else {
        const { data } = await api.post('/surgical/discharge/create', form);
        setDischargeId(data.id); setViewToken(data.view_token || '');
        toast.success('Draft saved');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const sealFile = async () => {
    if (!dischargeId) { toast.error('Save the draft first'); return; }
    setSaving(true);
    try {
      await api.post(`/surgical/discharge/${dischargeId}/seal`);
      setStatus('sealed'); if (dictation.live) dictation.stop();
      toast.success('Discharge summary sealed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Seal failed'); }
    finally { setSaving(false); }
  };

  const openPdf = async () => {
    if (!dischargeId) { toast.error('Save the draft first to generate a PDF'); return; }
    try {
      const res = await api.get(`/surgical/discharge/${dischargeId}/pdf` + (compactPdf ? '?compact=1' : ''), { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, '_blank');
      if (w) setTimeout(() => { try { w.print(); } catch {} }, 800);
    } catch (e) { toast.error(e?.response?.data?.detail || 'PDF render failed'); }
  };

  // ── Voice challenge ──
  const startChallenge = async () => {
    if (!activeProfile) { toast.error('Pick a voice profile first'); return; }
    try {
      const { data } = await api.get(`/surgical/voice-profile/${activeProfile.id}/challenge-phrase`);
      setChallenge(data);
      toast.info('Read the challenge phrase aloud to verify your voice');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start challenge'); }
  };
  const recordChallenge = async () => {
    if (!challenge || !activeProfile) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const b64 = await new Promise((r) => { const rd = new FileReader(); rd.onload = () => r(rd.result); rd.readAsDataURL(blob); });
        setVerifyingChallenge(true);
        try {
          const { data } = await api.post(`/surgical/voice-profile/${activeProfile.id}/challenge-verify`, {
            challenge_id: challenge.challenge_id, live_sample_b64: b64, live_mime: 'audio/webm',
          });
          setSessionToken(data.session_token); setSessionExpiresAt(data.session_expires_at); setChallenge(null);
          toast.success('Voice verified — dictation unlocked for 30 minutes');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Voice verification failed'); }
        finally { setVerifyingChallenge(false); stream.getTracks().forEach((t) => t.stop()); }
      };
      mr.start();
      setTimeout(() => { try { mr.stop(); } catch {} }, 6000);
    } catch { toast.error('Microphone unavailable'); }
  };

  // ── Photos ──
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const uploadPhotos = async (files) => {
    if (!dischargeId) { toast.error('Save draft first, then attach photos'); return; }
    setUploadingPhoto(true);
    try {
      for (const f of Array.from(files || []).slice(0, 15)) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} exceeds 10 MB`); continue; }
        const b64 = await new Promise((r) => { const rd = new FileReader(); rd.onload = () => r(rd.result); rd.readAsDataURL(f); });
        const { data } = await api.post(`/surgical/discharge/${dischargeId}/photo`, { photo_b64: b64, mime: f.type || 'image/jpeg' });
        set('intra_op_photos', [...(form.intra_op_photos || []), data.url]);
      }
      toast.success('Photo(s) uploaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingPhoto(false); }
  };
  const removePhoto = async (url) => {
    try {
      await api.delete(`/surgical/discharge/${dischargeId}/photo`, { params: { url } });
      set('intra_op_photos', (form.intra_op_photos || []).filter((p) => p !== url));
    } catch { toast.error('Could not remove photo'); }
  };

  // ── Analytics ──
  const [analytics, setAnalytics] = useState({ by_surgery: [], total_cases: 0 });
  const [showAnalytics, setShowAnalytics] = useState(false);
  useEffect(() => { (async () => { try { const { data } = await api.get('/surgical/analytics'); setAnalytics(data); } catch {} })(); }, [dischargeId]);
  const openCases = async () => {
    try {
      const { data } = await api.get('/surgical/discharge/list');
      setDischargesList(data.rows || []);
      setCasesOpen(true);
    } catch {
      toast.error('Could not load cases list');
    }
  };

  const startNewDraft = async () => {
    try { await draftStore.removeItem(DRAFT_KEY); } catch {}
    setForm(emptyForm);
    setDischargeId('');
    setViewToken('');
    setStatus('draft');
    setIsDemo(false);
    setResidentReg('');
    if (settings.principal_name) {
      const line = [settings.principal_name, settings.principal_qualifications].filter(Boolean).join(', ');
      setForm((f) => ({ ...f, principal_surgeon_name: line }));
    }
    const def = (settings.hospitals || []).find((h) => h.id === settings.default_hospital_id) || (settings.hospitals || [])[0];
    if (def) applyHospital(def);
    setCasesOpen(false);
    toast.success('Started new blank discharge');
  };

  const drillIntoDischarge = async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/surgical/discharge/${id}`);
      setForm({
        hospital_name: data.hospital_name || '', department: data.department || '', location: data.location || '',
        patient_name: data.patient_name || '', patient_sex: data.patient_sex || '', patient_age: data.patient_age || '',
        patient_cr_no: data.patient_cr_no || '', patient_address: data.patient_address || '', patient_mobile: '',
        principal_surgeon_name: data.principal_surgeon_name || '', co_residents: data.co_residents || [],
        surgery_catalog_id: data.surgery_catalog_id || '', operation_performed: data.operation_performed || '',
        diagnosis: data.diagnosis || '', operative_findings: data.operative_findings || [], post_op_plan: data.post_op_plan || '',
        treatments_advised: data.treatments_advised || [], followup_schedule: data.followup_schedule || [],
        intra_op_photos: data.intra_op_photos || [], attachments: data.attachments || [],
      });
      setDischargeId(data.id); setViewToken(data.view_token || ''); setStatus(data.status || 'draft'); setShowAnalytics(false);
      setIsDemo(!!data.is_demo); setResidentReg(data.resident_reg_no || '');
      toast.success('Loaded discharge for review');
    } catch { toast.error('Could not load that discharge'); }
  };

  // ── Share modal ──
  const [shareOpen, setShareOpen] = useState(false);
  const [shareRecipient, setShareRecipient] = useState({ name: '', email: '', mobile: '', note: '', ttl_days: 14 });
  const [shareLink, setShareLink] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [shareHistory, setShareHistory] = useState([]);
  const loadShares = async (id) => {
    if (!id) { setShareHistory([]); return; }
    try { const { data } = await api.get(`/surgical/discharge/${id}/shares`); setShareHistory(data.rows || []); } catch {}
  };
  const openShare = async () => {
    if (!dischargeId || status !== 'sealed') { toast.error('Seal the discharge before sharing'); return; }
    setShareOpen(true); setShareLink(''); await loadShares(dischargeId);
  };
  const createShare = async () => {
    if (!shareRecipient.name.trim()) { toast.error('Recipient name is required'); return; }
    setShareBusy(true);
    try {
      const { data } = await api.post(`/surgical/discharge/${dischargeId}/share`, {
        recipient_name: shareRecipient.name.trim(), recipient_email: shareRecipient.email.trim(),
        recipient_mobile: shareRecipient.mobile.trim(), note: shareRecipient.note.trim(),
        ttl_days: Number(shareRecipient.ttl_days) || 14,
      });
      setShareLink(`${window.location.origin}/surgical-share/${data.share_token}`);
      await loadShares(dischargeId);
      toast.success('Share link ready — copy and send');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Share failed'); }
    finally { setShareBusy(false); }
  };
  const revokeShare = async (share_id) => {
    try { await api.post(`/surgical/discharge-share/${share_id}/revoke`); await loadShares(dischargeId); toast.success('Share revoked'); }
    catch { toast.error('Could not revoke'); }
  };

  // ── Sync queue ──
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState(0);
  const refreshPending = async () => { try { setPendingCount((await syncQueue.keys()).length); } catch { setPendingCount(0); } };
  const drainQueue = async () => {
    try {
      for (const k of await syncQueue.keys()) {
        const item = await syncQueue.getItem(k);
        if (!item) { await syncQueue.removeItem(k); continue; }
        try { await api.request({ url: item.url, method: item.method, data: item.data }); await syncQueue.removeItem(k); }
        catch { break; }
      }
    } finally { await refreshPending(); }
  };
  useEffect(() => {
    const on = () => { setOnline(true); drainQueue(); };
    const off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    refreshPending(); drainQueue();
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readOnly = status === 'sealed';
  const nowStr = new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });

  if (entitlement && !entitlement.active) {
    return <SurgicalGate status={entitlement} onActivated={() => refreshEntitlement()} />;
  }

  return (
    <div className="min-h-screen print:bg-white" style={{ background: '#e8eaef' }}>
      {showRecovery && dischargeId && <RecoveryPanel dischargeId={dischargeId} sealed={status === 'sealed'} onClose={() => setShowRecovery(false)} />}
      {/* ── App toolbar ── */}
      <div className="sticky top-0 z-40 print:hidden" style={{ background: NAVY }}>
        <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-white/60 hover:text-white" data-testid="surg-back-dashboard"><ArrowLeft size={17} /></Link>
            <span className="text-[10px] font-bold tracking-[.18em] uppercase" style={{ color: '#4ade80' }}>Surgical Suite</span>
            <span className="text-white text-sm font-semibold">Post-Op Discharge Summary</span>
            {entitlement?.active && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border border-emerald-300/40 bg-emerald-500/15 text-emerald-100" data-testid="surg-ent-chip">
                {entitlement.days_left}d left · {entitlement.discharges_left}/{entitlement.max_discharges_per_period} discharges
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={toggleDictation} disabled={readOnly}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-bold text-white disabled:opacity-50"
                    style={{ background: dictation.live ? '#ef4444' : TEAL }} data-testid="surg-master-dictate">
              {dictation.live ? <><MicOff size={13} /> Stop dictation</> : <><Mic size={13} /> Dictate summary</>}
            </button>
            <button type="button" onClick={saveDraft} disabled={saving || readOnly}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/85 border border-white/25 hover:bg-white/10 disabled:opacity-50" data-testid="surg-save-draft">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save draft
            </button>
            <button type="button" onClick={sealFile} disabled={saving || readOnly || !dischargeId}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/85 border border-white/25 hover:bg-white/10 disabled:opacity-50" data-testid="surg-seal-file">
              <Lock size={12} /> {status === 'sealed' ? 'Sealed' : 'Seal & lock'}
            </button>
            <button type="button" onClick={openShare} disabled={status !== 'sealed'}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/85 border border-white/25 hover:bg-white/10 disabled:opacity-50" data-testid="surg-share">
              <Share2 size={12} /> Share
            </button>
            <button type="button" onClick={() => setShowRecovery(true)} disabled={status !== 'sealed' || !dischargeId}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/85 border border-white/25 hover:bg-white/10 disabled:opacity-50" data-testid="surg-recovery-plan">
              <HeartPulse size={12} /> Recovery Plan & Rx
            </button>
            <RecoveryMonitorButton />
            <button type="button" onClick={openCases}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/85 border border-white/25 hover:bg-white/10" data-testid="surg-cases-toggle">
              <FileText size={12} /> Cases / Patients
            </button>
            <button type="button" onClick={() => setShowAnalytics((v) => !v)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/85 border border-white/25 hover:bg-white/10" data-testid="surg-analytics-toggle">
              <BarChart3 size={12} /> Analytics
            </button>
            <button type="button" onClick={openPdf} disabled={!dischargeId}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/85 border border-white/25 hover:bg-white/10 disabled:opacity-50" data-testid="surg-pdf">
              <FileDown size={12} /> PDF & print
            </button>
            <label className="inline-flex items-center gap-1 text-[10px] font-semibold text-white/70 cursor-pointer select-none" data-testid="surg-compact-toggle">
              <input type="checkbox" checked={compactPdf} onChange={(e) => setCompactPdf(e.target.checked)} className="accent-emerald-400" /> Fit to 1 page
            </label>
            <button type="button" onClick={() => setSettingsOpen(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/85 border border-white/25 hover:bg-white/10" data-testid="surg-settings-open">
              <Settings2 size={12} /> Unit settings
            </button>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold"
                  style={{ background: online ? 'rgba(16,185,129,.2)' : 'rgba(245,158,11,.25)', color: online ? '#6ee7b7' : '#fcd34d' }} data-testid="surg-sync-status">
              {online ? <Wifi size={10} /> : <WifiOff size={10} />}{online ? 'Online' : 'Offline'}
              {pendingCount > 0 && <span>· {pendingCount}</span>}
              {pendingCount > 0 && online && <button type="button" onClick={drainQueue} className="ml-0.5"><RefreshCcw size={9} /></button>}
            </span>
          </div>
        </div>
      </div>

      {/* ── Live dictation banner ── */}
      {dictation.live && (
        <div className="sticky top-[46px] z-30 print:hidden" style={{ background: '#fef3c7', borderBottom: '1px solid #f59e0b' }}>
          <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-[11px] font-bold text-red-600 shrink-0">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LISTENING
            </span>
            <span className="text-[12px] text-amber-900 italic truncate" data-testid="surg-interim-line">
              {interimText ? `“${interimText}”` : 'Speak a section cue — "Diagnosis", "Operative findings", "Post-op plan"…'}
            </span>
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-amber-700 shrink-0" data-testid="surg-active-field">
              Field: {FIELD_LABELS[activeTarget]}
            </span>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-5">
        {/* ══════════ THE DOCUMENT SHEET ══════════ */}
        <div className="relative print:shadow-none print:border-2"
             style={{ background: PAPER, border: `2px solid ${NAVY}`, borderRadius: 2,
                      boxShadow: '0 25px 60px -12px rgba(7,18,47,.25), 0 0 0 6px #fdfcf7, 0 0 0 7px #c7cdd9',
                      padding: '40px 48px' }} data-testid="surg-document-sheet">

          {/* Faint centre watermark behind the document (logo in the letterhead row below) */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden" aria-hidden="true">
            {settings.logo_url && (
              <img src={settings.logo_url} alt="" style={{ width: '60%', opacity: 0.08 }} data-testid="surg-watermark-logo" />
            )}
            {status !== 'sealed' && (
              <div className="absolute" style={{ fontFamily: mono, fontSize: 42, fontWeight: 800, letterSpacing: '.18em', color: 'rgba(148,163,184,.30)', transform: 'rotate(-24deg)', whiteSpace: 'nowrap' }} data-testid="surg-draft-watermark">DRAFT · NOT SEALED</div>
            )}
          </div>

          {/* CR No — top-right reserved badge */}
          <div className="absolute top-4 right-5 print:top-4 print:right-5">
            <div className="inline-block px-2.5 py-1" style={{ border: '1px dashed #94a3b8', background: '#f1f5f9', borderRadius: 2 }}>
              <span style={{ fontFamily: mono, fontSize: 10.5, color: '#64748b', letterSpacing: '.06em' }} data-testid="surg-cr-badge">
                CR NO{form.patient_cr_no ? ` · ${form.patient_cr_no}` : ' · RESERVED · HMS SYNC'}
              </span>
            </div>
          </div>

          {/* ── LEVEL 2: Hospital letterhead (FROZEN from Unit Settings) ── */}
          <div className="pb-4" style={{ borderBottom: `3px double ${NAVY}` }}>
            <div className="flex items-center gap-4">
              {settings.logo_url
                ? <img src={settings.logo_url} alt="Hospital logo" className="object-contain shrink-0"
                       style={{ width: 240, maxHeight: 82 }} data-testid="surg-letterhead-logo" />
                : <div style={{ width: 240 }} className="shrink-0" />}
              <div className="flex-1 min-w-0 text-center">
                {selectedHospital ? (
                  <>
                    <div style={{ fontFamily: serif, fontSize: 25, fontWeight: 700, color: NAVY }} data-testid="surg-hospital-name">{selectedHospital.name}</div>
                    <div style={{ fontSize: 12, color: '#475569', marginTop: 3 }}>
                      {selectedHospital.department || form.department || 'Department'} · {selectedHospital.building_address || selectedHospital.location || form.location}
                    </div>
                    <div style={{ fontFamily: mono, fontSize: 10.5, color: '#64748b', marginTop: 2 }}>Document created: {nowStr} IST</div>
                  </>
                ) : (
                  <button type="button" onClick={() => setSettingsOpen(true)}
                          className="mx-auto block px-4 py-2.5 rounded-lg border-2 border-dashed text-[13px] font-semibold print:hidden"
                          style={{ borderColor: TEAL, color: TEAL }} data-testid="surg-setup-hospital">
                    + Configure your hospital & unit in Unit Settings — the letterhead is locked to it
                  </button>
                )}
              </div>
              <div style={{ width: 120 }} className="shrink-0" />
            </div>
            {(settings.hospitals || []).length > 1 && (
              <div className="mt-2 text-center print:hidden">
                <select value={selectedHospitalId} onChange={(e) => applyHospital(settings.hospitals.find((h) => h.id === e.target.value))}
                        className="px-3 py-1.5 rounded-lg border border-slate-300 text-[12px] font-semibold bg-white"
                        data-testid="surg-hospital-picker">
                  {(settings.hospitals || []).map((h) => <option key={h.id} value={h.id}>{h.name} — {h.department}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* ── Document title (centred, prominent) ── */}
          <div className="text-center pt-4 pb-1" data-testid="surg-doc-title">
            <div style={{ fontFamily: serif, fontSize: 26, fontWeight: 800, color: NAVY, letterSpacing: '.12em' }}>DISCHARGE SUMMARY{isDemo && <sup style={{ color: '#dc2626', fontSize: 12, fontWeight: 800, letterSpacing: '.08em' }} data-testid="surg-dummy-tag">DUMMY</sup>}</div>
            <div style={{ fontSize: 11, color: '#64748b', letterSpacing: '.06em', marginTop: 2 }}>Post-Operative · IPD</div>
          </div>

          {/* ── LEVEL 1: Admitted Under (anchor) ── */}
          <div className="pt-3 pb-3" style={{ borderBottom: `1.5px solid ${NAVY}` }}>
            <div className="text-center" style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.14em', color: TEAL, textTransform: 'uppercase' }}>Admitted Under</div>
            <div className="flex items-baseline justify-center gap-2" style={{ flexWrap: 'nowrap' }}>
              <input value={form.principal_surgeon_name} onChange={(e) => set('principal_surgeon_name', e.target.value)}
                     placeholder="Principal Surgeon — name & qualifications" readOnly={readOnly}
                     className="bg-transparent outline-none print:border-0 text-center"
                     size={Math.max(20, (form.principal_surgeon_name || '').length)}
                     style={{ fontFamily: serif, fontSize: 17, fontWeight: 700, color: NAVY, borderBottom: '1px dashed transparent' }}
                     data-testid="surg-principal-surgeon" />
              {settings.principal_reg_no && (
                <span style={{ fontSize: 10.5, color: '#475569', whiteSpace: 'nowrap' }} data-testid="surg-principal-reg">· Reg. No: {settings.principal_reg_no}</span>
              )}
            </div>
          </div>

          {/* ── LEVEL 3: Patient identity table ── */}
          <table className="w-full mt-4" style={{ borderCollapse: 'collapse', fontSize: 12.5 }}>
            <tbody>
              <tr>
                <Pcell label="Patient Name" width="34%"><PlainInput value={form.patient_name} onChange={(v) => set('patient_name', v)} readOnly={readOnly} testid="surg-patient-name" /></Pcell>
                <Pcell label="Age / Sex" width="22%">
                  <div className="flex gap-1.5">
                    <PlainInput value={form.patient_age} onChange={(v) => set('patient_age', v)} readOnly={readOnly} placeholder="Age" testid="surg-patient-age" small />
                    <select value={form.patient_sex} onChange={(e) => set('patient_sex', e.target.value)} disabled={readOnly}
                            className="bg-transparent outline-none text-[13px] font-semibold" style={{ color: NAVY }} data-testid="surg-patient-sex">
                      <option value="">—</option><option>Male</option><option>Female</option><option>Other</option>
                    </select>
                  </div>
                </Pcell>
                <Pcell label="Mobile" width="22%" mono><PlainInput value={form.patient_mobile} onChange={(v) => set('patient_mobile', v)} readOnly={readOnly} testid="surg-patient-mobile" /></Pcell>
                <Pcell label="Date of Discharge" width="22%" mono><span style={{ color: NAVY, fontWeight: 600 }}>{nowStr.split(',')[0]}</span></Pcell>
              </tr>
              <tr>
                <td colSpan={4} style={{ border: '1px solid #94a3b8', padding: '6px 10px' }}>
                  <div className="flex items-baseline gap-2">
                    <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.1em', color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Address</span>
                    <PlainInput value={form.patient_address} onChange={(v) => set('patient_address', v)} readOnly={readOnly} grow testid="surg-patient-address" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {/* ── Operation performed (catalog picker) ── */}
          <DocSection title="Operation Performed" active={dictation.live && activeTarget === 'operation_performed'}>
            <div className="flex items-center gap-2">
              <PlainInput value={form.operation_performed} onChange={(v) => set('operation_performed', v)} readOnly={readOnly}
                          placeholder="Type or pick from your catalog…" testid="surg-operation-input" grow />
              {!readOnly && (
                <button type="button" onClick={() => setShowCatalog((v) => !v)} className="text-[11px] font-bold px-2.5 py-1 rounded-full border print:hidden"
                        style={{ color: TEAL, borderColor: TEAL }} data-testid="surg-catalog-toggle">
                  {showCatalog ? 'Close' : 'Catalog'}
                </button>
              )}
            </div>
            {showCatalog && !readOnly && (
              <div className="mt-2 space-y-1.5 print:hidden">
                <div className="flex gap-2">
                  <input type="text" placeholder="Add a new surgery to your catalog" value={newSurgeryName} onChange={(e) => setNewSurgeryName(e.target.value)}
                         className="flex-1 px-2.5 py-1.5 rounded-lg border border-slate-300 text-[13px] outline-none" data-testid="surg-catalog-new-name" />
                  <button type="button" onClick={addSurgery} className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white" style={{ background: TEAL }} data-testid="surg-catalog-new-add"><Plus size={13} /></button>
                </div>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg bg-white divide-y divide-slate-100">
                  {catalog.length === 0 && <div className="p-2 text-[12px] text-slate-500 italic">No surgeries in your catalog yet.</div>}
                  {catalog.map((c) => (
                    <button key={c.id} type="button" onClick={() => pickSurgery(c.id)}
                            className={`w-full text-left px-2.5 py-1.5 text-[12px] hover:bg-slate-50 ${form.surgery_catalog_id === c.id ? 'bg-teal-50 font-semibold' : ''}`}
                            data-testid={`surg-catalog-pick-${c.id}`}>{c.surgery_name}</button>
                  ))}
                </div>
              </div>
            )}
          </DocSection>

          <DocSection title="Diagnosis" active={dictation.live && activeTarget === 'diagnosis'}>
            <DocTextArea value={form.diagnosis} onChange={(v) => set('diagnosis', v)} readOnly={readOnly} testid="surg-diagnosis-input" rows={2} />
          </DocSection>

          <DocSection title="Operative Findings" active={dictation.live && activeTarget === 'operative_findings'}>
            <BulletEditor items={form.operative_findings} onChange={(v) => set('operative_findings', v)} readOnly={readOnly} target="operative_findings" />
          </DocSection>

          <DocSection title="Post-Operative Plan" active={dictation.live && activeTarget === 'post_op_plan'}>
            <DocTextArea value={form.post_op_plan} onChange={(v) => set('post_op_plan', v)} readOnly={readOnly} testid="surg-post-op-input" rows={3} />
          </DocSection>

          <DocSection title="Treatments Advised on Discharge" active={dictation.live && activeTarget === 'treatments_advised'}>
            <BulletEditor items={form.treatments_advised} onChange={(v) => set('treatments_advised', v)} readOnly={readOnly} target="treatments_advised" />
          </DocSection>

          <DocSection title="Follow-Up Schedule" active={dictation.live && activeTarget === 'followup_schedule'}>
            <div className="space-y-1.5">
              {(form.followup_schedule || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input type="date" value={item.date || ''} disabled={readOnly}
                         onChange={(e) => { const n = [...form.followup_schedule]; n[idx] = { ...n[idx], date: e.target.value }; set('followup_schedule', n); }}
                         className="px-2 py-1 rounded border border-slate-300 text-[12.5px] outline-none" style={{ fontFamily: mono, color: TEAL, fontWeight: 700 }}
                         data-testid={`surg-followup-date-${idx}`} />
                  <PlainInput value={item.action || ''} readOnly={readOnly}
                              onChange={(v) => { const n = [...form.followup_schedule]; n[idx] = { ...n[idx], action: v }; set('followup_schedule', n); }}
                              placeholder="Action / instruction" testid={`surg-followup-action-${idx}`} grow />
                  {!readOnly && <button type="button" onClick={() => set('followup_schedule', form.followup_schedule.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-rose-500 print:hidden"><Trash2 size={13} /></button>}
                </div>
              ))}
              {!readOnly && (
                <button type="button" onClick={() => set('followup_schedule', [...(form.followup_schedule || []), { date: '', action: '' }])}
                        className="text-[12px] font-semibold inline-flex items-center gap-1 print:hidden" style={{ color: TEAL }} data-testid="surg-followup-add">
                  <Plus size={12} /> Add follow-up row
                </button>
              )}
            </div>
          </DocSection>

          {/* ── Intra-op photos (screen only) ── */}
          <div className="mt-5 print:hidden">
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: NAVY, borderBottom: `2px solid ${NAVY}`, paddingBottom: 2, marginBottom: 8 }}>Intra-op Photos</div>
            <div className="flex flex-wrap gap-2">
              {(form.intra_op_photos || []).map((url, i) => (
                <div key={i} className="relative w-20 h-20 rounded border border-slate-300 overflow-hidden bg-slate-100">
                  <img src={url} alt={`intra-op ${i + 1}`} className="w-full h-full object-cover" />
                  {!readOnly && <button type="button" onClick={() => removePhoto(url)} className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5 text-rose-500" data-testid={`surg-photo-remove-${i}`}><X size={12} /></button>}
                </div>
              ))}
              {!readOnly && (
                <label className={`w-20 h-20 rounded border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-teal-400 ${uploadingPhoto ? 'opacity-60 pointer-events-none' : ''}`} data-testid="surg-photo-add">
                  {uploadingPhoto ? <Loader2 size={16} className="animate-spin" style={{ color: TEAL }} /> : <ImageIcon size={16} className="text-slate-400" />}
                  <span className="text-[10px] text-slate-500 mt-1">{uploadingPhoto ? 'Uploading' : 'Add photo'}</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(e.target.files)} disabled={!dischargeId} />
                </label>
              )}
            </div>
            {!dischargeId && <div className="text-[10px] text-slate-400 italic mt-1">Save the draft first to attach photos</div>}
          </div>

          {/* ── Signature + QR verification block ── */}
          <div className="flex items-end justify-between mt-10 pt-4" style={{ borderTop: `1.5px solid ${NAVY}` }}>
            <div>
              <div style={{ height: 32 }} />
              <div style={{ borderTop: '1px solid #334155', paddingTop: 3, fontSize: 11, color: '#334155' }}>
                <b>{activeProfile ? activeProfile.person_name : 'Resident on Duty'}</b> — {activeProfile ? activeProfile.person_role.replace('_', ' ') : 'Junior / Senior Resident'} (signatory)
                <div style={{ fontSize: 10, color: '#64748b' }}>Prepared under supervision of the Principal Surgeon</div>
                {(activeProfile?.reg_no || residentReg) && (
                  <div style={{ fontSize: 9.5, color: '#475569', marginTop: 1 }} data-testid="surg-resident-reg">RMP Reg. No: {activeProfile?.reg_no || residentReg}</div>
                )}
              </div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center" style={{ width: 66, height: 66, border: `1.5px solid ${NAVY}`, background: '#fff', borderRadius: 2 }}>
                {qrDataUrl
                  ? <img src={qrDataUrl} alt="QR verify" style={{ width: 60, height: 60 }} data-testid="surg-qr" />
                  : <span style={{ fontFamily: mono, fontSize: 8.5, color: '#94a3b8', textAlign: 'center', lineHeight: 1.3 }}>QR after<br />seal</span>}
              </div>
              <div style={{ fontSize: 9, color: '#64748b', marginTop: 3, letterSpacing: '.08em', textTransform: 'uppercase' }}>Scan to verify</div>
            </div>
            <div className="text-right">
              <div style={{ height: 32 }} />
              <div style={{ borderTop: '1px solid #334155', paddingTop: 3, fontSize: 11, color: '#334155' }}>
                <b>{form.principal_surgeon_name || 'Principal Surgeon'}</b>
                <div style={{ fontSize: 10, color: '#64748b' }}>Principal Surgeon · {selectedHospital?.department || form.department || 'Department'}</div>
              </div>
            </div>
          </div>

          {/* ── Footer branding ── */}
          <div className="mt-6 pt-3 text-center" style={{ borderTop: '1px dashed #94a3b8' }}>
            <div className="flex items-center justify-center gap-2" style={{ whiteSpace: 'nowrap' }}>
              <img src="/docsite_brand.png" alt="DocSite" style={{ height: 22 }} data-testid="surg-footer-brand" />
              <span style={{ fontSize: 10.5, fontWeight: 700, color: NAVY, letterSpacing: '.04em' }}>Built with <span style={{ color: '#dc2626' }}>♥</span> in India · DocSite Clinical Management Platform · <span style={{ color: TEAL }}>docsite.care</span></span>
            </div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>
              {selectedHospital ? `${selectedHospital.name} · ${selectedHospital.building_address || selectedHospital.location || ''}` : 'Hospital letterhead pending Unit Settings'}
            </div>
          </div>

          {status === 'sealed' && (
            <div className="absolute top-16 -right-2 px-3 py-1 text-white text-[10px] font-bold tracking-[.15em] rotate-45 print:hidden"
                 style={{ background: TEAL, boxShadow: '0 2px 6px rgba(0,0,0,.2)' }} data-testid="surg-sealed-ribbon">SEALED</div>
          )}
        </div>

        {/* ── Side rail: voice team + analytics + cue help ── */}
        <div className="space-y-4 print:hidden">
          <VoiceTeamPanel profiles={profiles} onRefresh={loadProfiles} activeProfileId={activeProfile?.id}
                          onPick={setActiveProfile} sessionToken={sessionToken}
                          onStartChallenge={startChallenge} challenge={challenge} onRecordChallenge={recordChallenge}
                          verifyingChallenge={verifyingChallenge} />
          <div className="border border-slate-200 rounded-xl p-3 bg-white">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Dictation cues</div>
            <div className="text-[11px] text-slate-600 leading-relaxed">
              Say a section name to jump fields: <i>"Diagnosis…"</i>, <i>"Operation performed…"</i>, <i>"Operative findings…"</i>, <i>"Post-op plan…"</i>, <i>"Treatments advised…"</i>, <i>"Follow-up…"</i>.
              {lastCue && <div className="mt-1.5 font-semibold" style={{ color: TEAL }}>Last jump → {lastCue}</div>}
            </div>
          </div>
          {showAnalytics && (
            <div className="border border-slate-200 rounded-xl p-3 bg-white" data-testid="surg-analytics-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[12px] font-bold" style={{ color: NAVY }}>{analytics.total_cases || 0} cases · {(analytics.by_surgery || []).length} surgery types</div>
                <button type="button" onClick={() => setShowAnalytics(false)} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                {(analytics.by_surgery || []).map((g) => (
                  <div key={g.surgery_catalog_id || g.surgery_name} className="flex items-center justify-between py-1.5 text-[12px]">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate" style={{ color: NAVY }}>{g.surgery_name}</div>
                      <div className="text-[10px] text-slate-500">{g.count} case{g.count === 1 ? '' : 's'} · {String(g.latest_at || '').slice(0, 10)}</div>
                    </div>
                    <button type="button" onClick={() => drillIntoDischarge(g.latest_discharge_id)} className="text-[10px] font-bold px-2 py-0.5 rounded-full border"
                            style={{ color: TEAL, borderColor: TEAL }} data-testid={`surg-drill-${g.surgery_catalog_id || 'none'}`}>Review →</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Unit Settings drawer (hospitals — principal configures once) ── */}
      {settingsOpen && (
        <UnitSettingsDrawer settings={settings} onClose={() => setSettingsOpen(false)}
                            onLogoChange={(url) => setSettings((prev) => ({ ...prev, logo_url: url }))}
                            onSaved={async (s) => { setSettings((prev) => ({ ...prev, ...s })); setSettingsOpen(false);
                              const def = s.hospitals.find((h) => h.id === s.default_hospital_id) || s.hospitals[0];
                              if (def) applyHospital(def); }} />
      )}

      {/* ── Cases / Patient Selector Modal ── */}
      {casesOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 print:hidden" onClick={() => setCasesOpen(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="surg-cases-modal">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: TEAL }}>Surgical Suite</div>
                <h2 className="text-lg font-black" style={{ color: NAVY }}>Discharge Summaries & Demo Cases</h2>
              </div>
              <button onClick={() => setCasesOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="space-y-2 mb-4 max-h-72 overflow-y-auto">
              {dischargesList.map((d) => (
                <div key={d.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:border-slate-400 bg-slate-50 transition cursor-pointer"
                     onClick={() => { drillIntoDischarge(d.id); setCasesOpen(false); }} data-testid={`surg-case-item-${d.id}`}>
                  <div>
                    <div className="text-[13px] font-bold" style={{ color: NAVY }}>{d.patient_name || 'Unnamed Patient'}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      CR: {d.patient_cr_no || '—'} · {d.status === 'sealed' ? <span className="text-emerald-600 font-semibold">SEALED (with QR)</span> : <span className="text-amber-600 font-semibold">DRAFT</span>}
                    </div>
                  </div>
                  <button type="button" className="text-[11px] font-bold px-3 py-1 rounded-full border text-white" style={{ background: TEAL, borderColor: TEAL }}>
                    Load Case →
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={startNewDraft} className="w-full py-2.5 rounded-xl border-2 border-dashed text-[12px] font-bold text-slate-600 hover:text-slate-800" data-testid="surg-new-draft-btn">
              + Start a new blank draft
            </button>
          </div>
        </div>
      )}

      {/* ── Share modal ── */}
      {shareOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 print:hidden" onClick={() => setShareOpen(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="surg-share-modal">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: TEAL }}>Inter-Doctor Share</div>
                <h2 className="text-lg font-black" style={{ color: NAVY }}>Send this discharge to another doctor</h2>
              </div>
              <button onClick={() => setShareOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            {!shareLink ? (
              <div className="space-y-2.5">
                <input type="text" placeholder="Recipient doctor name *" value={shareRecipient.name} onChange={(e) => setShareRecipient({ ...shareRecipient, name: e.target.value })}
                       className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none" data-testid="surg-share-name" />
                <input type="email" placeholder="Recipient email (optional)" value={shareRecipient.email} onChange={(e) => setShareRecipient({ ...shareRecipient, email: e.target.value })}
                       className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none" data-testid="surg-share-email" />
                <input type="text" placeholder="Recipient mobile (optional)" value={shareRecipient.mobile} onChange={(e) => setShareRecipient({ ...shareRecipient, mobile: e.target.value })}
                       className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none" data-testid="surg-share-mobile" />
                <textarea rows={3} placeholder="Optional note to the recipient" value={shareRecipient.note} onChange={(e) => setShareRecipient({ ...shareRecipient, note: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none" data-testid="surg-share-note" />
                <div className="flex items-center gap-2">
                  <label className="text-[11px] text-slate-600">Link expires in</label>
                  <select value={shareRecipient.ttl_days} onChange={(e) => setShareRecipient({ ...shareRecipient, ttl_days: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-[13px]" data-testid="surg-share-ttl">
                    <option value={3}>3 days</option><option value={7}>7 days</option><option value={14}>14 days</option>
                    <option value={30}>30 days</option><option value={90}>90 days</option>
                  </select>
                </div>
                <button type="button" onClick={createShare} disabled={shareBusy} className="w-full py-2 rounded-lg text-white text-[13px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: NAVY }} data-testid="surg-share-create">
                  {shareBusy ? <><Loader2 size={13} className="animate-spin" /> Generating…</> : <><Share2 size={13} /> Generate secure link</>}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[12px] font-bold text-emerald-700">Share link ready — copy and send via WhatsApp / email / SMS</div>
                <div className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg bg-slate-50">
                  <input type="text" readOnly value={shareLink} className="flex-1 bg-transparent text-[12px] outline-none" data-testid="surg-share-link" />
                  <button type="button" onClick={() => { navigator.clipboard.writeText(shareLink); toast.success('Copied'); }} className="text-slate-500 hover:text-slate-900" data-testid="surg-share-copy"><Copy size={14} /></button>
                </div>
                <a href={`https://wa.me/?text=${encodeURIComponent(`Discharge summary — ${form?.patient_name || ''}: ${shareLink}`)}`} target="_blank" rel="noopener noreferrer" className="block text-center py-2 rounded-lg border border-slate-300 text-[13px] font-semibold hover:bg-slate-50" data-testid="surg-share-wa">Open WhatsApp</a>
                <button type="button" onClick={() => { setShareLink(''); setShareRecipient({ name: '', email: '', mobile: '', note: '', ttl_days: 14 }); }} className="w-full py-1.5 text-[12px] text-slate-500 hover:text-slate-800">Send to someone else</button>
              </div>
            )}
            {shareHistory.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-200">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-2">Active share history</div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {shareHistory.map((s) => (
                    <div key={s.id} className="flex items-center gap-2 text-[12px]">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.revoked ? 'bg-rose-500' : 'bg-emerald-500'}`}></span>
                      <span className="flex-1 truncate"><b>{s.recipient_name}</b> · {String(s.created_at || '').slice(0, 10)} · {s.opened_count || 0} opens</span>
                      {!s.revoked && <button type="button" onClick={() => revokeShare(s.id)} className="text-rose-600 hover:text-rose-800 text-[11px] font-bold" data-testid={`surg-share-revoke-${s.id}`}>Revoke</button>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Small document primitives ──
function Pcell({ label, children, width, mono: isMono }) {
  return (
    <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', width }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.1em', color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
      <div style={isMono ? { fontFamily: mono } : undefined}>{children}</div>
    </td>
  );
}
function PlainInput({ value, onChange, placeholder, readOnly, testid, small, inline, grow }) {
  return (
    <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} readOnly={readOnly}
           className={`bg-transparent outline-none ${grow ? 'flex-1 w-full' : inline ? 'inline-block min-w-[200px]' : 'w-full'}`}
           style={{ fontSize: small ? 12.5 : 13.5, fontWeight: 600, color: '#000000' }}
           data-testid={testid} />
  );
}
function DocTextArea({ value, onChange, readOnly, testid, rows }) {
  return (
    <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} readOnly={readOnly} rows={rows || 2}
              className="w-full bg-transparent outline-none resize-y"
              style={{ fontSize: 14, lineHeight: 1.65, color: '#000000' }}
              data-testid={testid} />
  );
}
function BulletEditor({ items, onChange, readOnly, target }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="space-y-1">
      {list.map((line, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <span style={{ color: TEAL, fontWeight: 800, fontSize: 13 }}>•</span>
          <PlainInput value={line} readOnly={readOnly}
                      onChange={(v) => { const n = [...list]; n[idx] = v; onChange(n); }}
                      testid={`surg-${target}-line-${idx}`} grow />
          {!readOnly && <button type="button" onClick={() => onChange(list.filter((_, i) => i !== idx))} className="text-slate-300 hover:text-rose-500 print:hidden"><X size={13} /></button>}
        </div>
      ))}
      {list.length === 0 && <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 13 }}>—</span>}
      {!readOnly && (
        <button type="button" onClick={() => onChange([...list, ''])} className="text-[11px] font-semibold inline-flex items-center gap-1 print:hidden" style={{ color: TEAL }} data-testid={`surg-${target}-add`}>
          <Plus size={11} /> Add bullet
        </button>
      )}
    </div>
  );
}
function DocSection({ title, children, active }) {
  return (
    <div className="mt-5" style={{ borderBottom: '1px solid #cbd5e1', paddingBottom: 14 }}>
      <div className="flex items-center gap-2">
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: NAVY, borderBottom: `2px solid ${active ? '#ef4444' : NAVY}`, paddingBottom: 2, marginBottom: 8 }}>
          {title}
        </div>
        {active && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold text-white mb-2" style={{ background: '#ef4444' }} data-testid="surg-recording-pill">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> RECORDING HERE
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// ── Voice Team Panel (with posting window + login provisioning) ──
function VoiceTeamPanel({ profiles, onRefresh, activeProfileId, onPick, sessionToken, onStartChallenge, challenge, onRecordChallenge, verifyingChallenge }) {
  const [name, setName] = useState('');
  const [role, setRole] = useState('sr_resident');
  const [postingStart, setPostingStart] = useState('');
  const [postingEnd, setPostingEnd] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [recording, setRecording] = useState(false);
  const [regNo, setRegNo] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  const startRec = async () => {
    if (!navigator.mediaDevices) { toast.error('Microphone not available'); return; }
    if (!name.trim()) { toast.error('Enter person name first'); return; }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
    const chunks = [];
    mr.ondataavailable = (e) => chunks.push(e.data);
    mr.onstop = async () => {
      const blob = new Blob(chunks, { type: 'audio/webm' });
      const b64 = await new Promise((r) => { const rd = new FileReader(); rd.onload = () => r(rd.result); rd.readAsDataURL(blob); });
      setEnrolling(true);
      try {
        const { data: prof } = await api.post('/surgical/voice-profile/enroll', {
          person_name: name.trim(), person_role: role, reg_no: regNo.trim(), voice_sample_b64: b64, voice_mime: 'audio/webm',
        });
        if (postingStart || postingEnd) {
          await api.put(`/surgical/voice-profile/${prof.id}/posting`, { posting_start: postingStart, posting_end: postingEnd, posting_note: '' });
        }
        if (loginEmail.trim() && loginPassword) {
          await api.put(`/surgical/voice-profile/${prof.id}/set-login`, { login_email: loginEmail.trim(), login_password: loginPassword });
        }
        toast.success('Voice profile enrolled');
        setName(''); setRegNo(''); setLoginEmail(''); setLoginPassword(''); setPostingStart(''); setPostingEnd('');
        await onRefresh();
      } catch (e) { toast.error(e?.response?.data?.detail || 'Enrollment failed'); }
      finally { setEnrolling(false); stream.getTracks().forEach((t) => t.stop()); }
    };
    mr.start(); setRecording(true);
    setTimeout(() => { try { mr.stop(); setRecording(false); } catch {} }, 15000);
  };

  const toggleActive = async (p) => {
    try {
      await api.post(`/surgical/voice-profile/${p.id}/${p.is_active ? 'revoke' : 'reactivate'}`);
      toast.success(p.is_active ? 'Deactivated (records preserved)' : 'Reactivated');
      await onRefresh();
    } catch { toast.error('Action failed'); }
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-bold" style={{ color: NAVY }}>Authorized Voice Team</h3>
        <span className="text-[11px] text-slate-500">{profiles.length} enrolled</span>
      </div>
      <div className="space-y-2 mb-3 max-h-56 overflow-y-auto pr-1">
        {profiles.length === 0 && <div className="text-[12px] text-slate-500 italic">No residents enrolled yet. Add the first below.</div>}
        {profiles.map((p) => {
          const ended = p.posting_end && p.posting_end < new Date().toISOString().slice(0, 10);
          return (
            <div key={p.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border ${p.id === activeProfileId ? 'border-teal-400 bg-teal-50' : 'border-slate-200 bg-white'}`}>
              <button type="button" onClick={() => onPick(p)} className="text-left flex-1 min-w-0">
                <div className="text-[13px] font-semibold truncate" style={{ color: NAVY }}>{p.person_name}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                  {p.person_role.replace('_', ' ')} · {ended ? 'Tenure ended' : p.is_active ? 'Active' : 'Deactivated'}
                  {p.posting_end ? ` · till ${p.posting_end}` : ''}
                </div>
              </button>
              <button type="button" onClick={() => toggleActive(p)} title={p.is_active ? 'Deactivate' : 'Reactivate'}
                      className={`${p.is_active ? 'text-slate-400 hover:text-rose-600' : 'text-slate-400 hover:text-teal-600'}`}
                      data-testid={`surg-profile-toggle-${p.id}`}>
                {p.is_active ? <ShieldOff size={14} /> : <ShieldCheck size={14} />}
              </button>
            </div>
          );
        })}
      </div>

      {activeProfileId && !sessionToken && (
        <button type="button" onClick={onStartChallenge} className="w-full mb-3 py-1.5 rounded-lg text-white text-[11px] font-bold flex items-center justify-center gap-1.5" style={{ background: NAVY }} data-testid="surg-start-challenge">
          <KeyRound size={11} /> Verify voice to dictate
        </button>
      )}
      {challenge && (
        <div className="border-2 border-amber-300 bg-amber-50 rounded-xl p-3 space-y-2 mb-3" data-testid="surg-challenge-panel">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Say this phrase aloud</div>
          <div className="text-[14px] font-serif italic text-slate-800">"{challenge.phrase}"</div>
          <button type="button" onClick={onRecordChallenge} disabled={verifyingChallenge} className="w-full py-2 rounded-lg text-white text-[12px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: NAVY }} data-testid="surg-record-challenge">
            {verifyingChallenge ? <><Loader2 size={12} className="animate-spin" /> Verifying…</> : <><Mic size={12} /> Record phrase (6s)</>}
          </button>
        </div>
      )}

      <div className="pt-3 border-t border-slate-200 space-y-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Enroll new resident</div>
        <input type="text" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)}
               className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-[13px] outline-none" data-testid="surg-enroll-name" />
        <input type="text" placeholder="RMP Reg. No. (Medical Council registration)" value={regNo} onChange={(e) => setRegNo(e.target.value)}
               className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-[13px] outline-none" data-testid="surg-enroll-regno" />
        <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-[13px] outline-none" data-testid="surg-enroll-role">
          <option value="principal">Principal Surgeon</option>
          <option value="sr_resident">Senior Resident</option>
          <option value="jr_resident">Junior Resident</option>
        </select>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Posting start</label>
            <input type="date" value={postingStart} onChange={(e) => setPostingStart(e.target.value)}
                   className="w-full px-2 py-1.5 rounded-lg border border-slate-300 text-[12px] outline-none" data-testid="surg-enroll-posting-start" />
          </div>
          <div>
            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Posting end (auto-deactivates)</label>
            <input type="date" value={postingEnd} onChange={(e) => setPostingEnd(e.target.value)}
                   className="w-full px-2 py-1.5 rounded-lg border border-slate-300 text-[12px] outline-none" data-testid="surg-enroll-posting-end" />
          </div>
        </div>
        <input type="email" placeholder="Login email (optional — for sub-account)" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
               className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-[12px] outline-none" data-testid="surg-enroll-email" />
        <input type="password" placeholder="Login password (min 8 chars)" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
               className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-[12px] outline-none" data-testid="surg-enroll-password" />
        <button type="button" onClick={startRec} disabled={recording || enrolling}
                className="w-full py-2 rounded-lg text-white text-[12px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: recording ? '#dc2626' : NAVY }} data-testid="surg-enroll-record">
          {enrolling ? <><Loader2 size={12} className="animate-spin" /> Enrolling…</>
            : recording ? <><MicOff size={12} /> Recording — auto-stops in 15s</>
            : <><UserPlus size={12} /> Record 5–15s voice & enroll</>}
        </button>
        <div className="text-[10px] text-slate-400 leading-relaxed flex gap-1">
          <ClipboardList size={11} className="shrink-0 mt-0.5" />
          Residents are deactivated — never deleted. Their past signatures stay legally valid.
        </div>
      </div>
    </div>
  );
}

// ── Unit Settings drawer — hospital postings (frozen letterhead source) ──
function UnitSettingsDrawer({ settings, onClose, onSaved, onLogoChange }) {
  const [hospitals, setHospitals] = useState(settings.hospitals?.length ? settings.hospitals : [{ id: '', name: '', department: '', location: '', building_address: '' }]);
  const [formErr, setFormErr] = useState('');
  const [defaultId, setDefaultId] = useState(settings.default_hospital_id || '');
  const [busy, setBusy] = useState(false);
  const [logoUrl, setLogoUrl] = useState(settings.logo_url || '');
  const [logoBusy, setLogoBusy] = useState(false);
  const [principalReg, setPrincipalReg] = useState(settings.principal_reg_no || '');

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    try {
      const b64 = await new Promise((r) => { const rd = new FileReader(); rd.onload = () => r(rd.result); rd.readAsDataURL(file); });
      const { data } = await api.post('/surgical/settings/logo', { logo_b64: b64, mime: file.type || 'image/png' });
      setLogoUrl(data.logo_url);
      onLogoChange?.(data.logo_url);
      toast.success('Hospital logo updated — letterhead, watermark and PDF now carry it');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Logo upload failed'); }
    finally { setLogoBusy(false); }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete('/surgical/settings/logo');
      setLogoUrl('');
      onLogoChange?.('');
      toast.success('Hospital logo removed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Remove failed'); }
    finally { setLogoBusy(false); }
  };
  const blank = { id: '', name: '', department: '', location: '', building_address: '' };

  const save = async () => {
    if (!hospitals.length || !hospitals[0].name.trim()) { setFormErr('Enter the hospital name in the first posting to save.'); return; }
    setFormErr('');
    setBusy(true);
    try {
      const { data } = await api.put('/surgical/settings', { hospitals, default_hospital_id: defaultId, principal_reg_no: principalReg.trim() });
      toast.success('Unit settings saved — letterhead is now locked');
      onSaved(data);
    } catch (e) { const m = e?.response?.data?.detail; setFormErr(typeof m === 'string' ? m : 'Save failed — please try again'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-end z-50 print:hidden" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="surg-settings-drawer">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: TEAL }}>Unit Settings</div>
            <h2 className="text-lg font-black" style={{ color: NAVY }}>Hospital postings</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">The discharge letterhead is <b>frozen</b> to this list. Voice entry cannot override it.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        {/* ── Hospital logo (letterhead + watermark source) ── */}
        <div className="border border-slate-200 rounded-xl p-3 mb-3" data-testid="surg-logo-section">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Hospital logo</div>
          <div className="flex items-center gap-3">
            {logoUrl
              ? <img src={logoUrl} alt="Hospital logo" className="h-12 w-12 object-contain rounded border border-slate-200 bg-white" data-testid="surg-logo-preview" />
              : <div className="h-12 w-12 rounded border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-300"><ImageIcon size={16} /></div>}
            <div className="flex-1 space-y-1.5">
              <div className="flex items-center gap-2">
                <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer border ${logoBusy ? 'opacity-60 pointer-events-none' : ''}`}
                       style={{ borderColor: TEAL, color: TEAL }} data-testid="surg-logo-upload">
                  {logoBusy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {logoUrl ? 'Replace logo' : 'Upload logo'}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                </label>
                {logoUrl && (
                  <button type="button" onClick={removeLogo} disabled={logoBusy}
                          className="text-[11px] font-semibold text-rose-500 hover:text-rose-600" data-testid="surg-logo-remove">Remove</button>
                )}
              </div>
              <p className="text-[10px] text-slate-400 leading-snug">PNG / JPG / WebP under 5 MB. Prints at the top-left corner and as a faint centre watermark on every page.</p>
            </div>
          </div>
        </div>

        {/* ── Principal surgeon MMC registration ── */}
        <div className="border border-slate-200 rounded-xl p-3 mb-3" data-testid="surg-reg-section">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Principal surgeon registration</div>
          <input type="text" value={principalReg} onChange={(e) => setPrincipalReg(e.target.value)}
                 placeholder="MMC / State Medical Council Reg. No"
                 className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-[12px] outline-none" data-testid="surg-principal-reg-input" />
          <p className="text-[10px] text-slate-400 mt-1 leading-snug">Printed next to your name in the “Admitted Under” row and the signature block.</p>
        </div>

        <div className="space-y-3">
          {hospitals.map((h, i) => (
            <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2 relative">
              <button type="button" onClick={() => { const n = hospitals.filter((_, j) => j !== i); setHospitals(n.length ? n : [{ ...blank }]); }}
                      className="absolute top-2 right-2 text-slate-300 hover:text-rose-500"><Trash2 size={13} /></button>
              <input value={h.name} placeholder="Hospital name *" onChange={(e) => { const n = [...hospitals]; n[i] = { ...n[i], name: e.target.value }; setHospitals(n); setFormErr(''); }}
                     className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-[13px] font-semibold outline-none" data-testid={`surg-settings-hname-${i}`} />
              <input value={h.department} placeholder="Department (e.g. Department of Urology)" onChange={(e) => { const n = [...hospitals]; n[i] = { ...n[i], department: e.target.value }; setHospitals(n); }}
                     className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-[12px] outline-none" data-testid={`surg-settings-hdept-${i}`} />
              <input value={h.location} placeholder="Location / City" onChange={(e) => { const n = [...hospitals]; n[i] = { ...n[i], location: e.target.value }; setHospitals(n); }}
                     className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-[12px] outline-none" data-testid={`surg-settings-hloc-${i}`} />
              <input value={h.building_address} placeholder="Building name + full address (letterhead line)" onChange={(e) => { const n = [...hospitals]; n[i] = { ...n[i], building_address: e.target.value }; setHospitals(n); }}
                     className="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-[12px] outline-none" data-testid={`surg-settings-haddr-${i}`} />
              <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                <input type="radio" name="default-hosp" checked={defaultId === h.id || (!defaultId && i === 0)}
                       onChange={() => setDefaultId(h.id)} data-testid={`surg-settings-hdefault-${i}`} />
                Default letterhead
              </label>
            </div>
          ))}
          {hospitals.length < 10 && (
            <button type="button" onClick={() => setHospitals([...hospitals, { ...blank }])}
                    className="w-full py-2 rounded-lg border-2 border-dashed text-[12px] font-semibold"
                    style={{ borderColor: TEAL, color: TEAL }} data-testid="surg-settings-add">
              <Plus size={12} className="inline -mt-0.5" /> Add another hospital posting
            </button>
          )}
          {formErr && <div className="text-[12px] font-semibold text-rose-600" data-testid="surg-settings-error">{formErr}</div>}
          <button type="button" onClick={save} disabled={busy}
                  className="w-full py-2.5 rounded-lg text-white text-[13px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ background: NAVY }} data-testid="surg-settings-save">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save unit settings
          </button>
        </div>
      </div>
    </div>
  );
}
